import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useScrollbarPosition } from '@rumblefish/ui/hooks/useScrollbarPosition';
import { Button, Section } from '@rumblefish/ui/Rumblefish23Theme';
import parse from 'html-react-parser';
import type { LegacyRef } from 'react';
import React from 'react';
import { Line } from './Line';
import type { OfferBoxProps, OfferDataProp, OfferProps } from './Offer.types';
import {
  StyledStack,
  StyledOfferBox,
  StyledOneElement,
  StyledTextWrapper,
  StyledRightWrapper,
  StyledOffersWrapper,
  NeonCircle,
  StyledTitle,
} from './styles';

export const OfferBox = ({
  description,
  title,
  customData,
  linkTo,
  linkText,
  isActive,
  height,
  step,
  stepColor,
}: OfferBoxProps) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorWhite = theme.palette.invertedContent?.PRIMARY;

  return (
    <StyledOfferBox isActive={isActive} height={height}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        {step ? (
          <NeonCircle color={stepColor || 'orange'}>{step}</NeonCircle>
        ) : null}
        <Typography color={colorWhite} marginBottom="" variant="strong_600">
          {title}
        </Typography>
      </Box>
      <Typography color={colorWhite} variant="regular_400">
        {customData ? customData : parse(description || '')}
      </Typography>
      {linkTo && (
        <Button variant="link" link={linkTo}>
          {isMobile ? 'See more' : linkText || 'See more'}
        </Button>
      )}
    </StyledOfferBox>
  );
};

export const Offer = React.forwardRef(function Ref(
  {
    sx,
    oneElementTitle,
    oneElementSubtitle,
    oneElementDescription,
    descMarginBottom,
    title,
    description,
    data,
    CustomButton,
    height,
    image,
    thresholds,
    ...props
  }: OfferProps & OfferDataProp,
  ref: LegacyRef<HTMLDivElement>
) {
  const theme = useTheme();

  const { containerRef, activeThreshold } = useScrollbarPosition();

  const colorWhite = theme.palette.invertedContent?.PRIMARY;

  const currentThresholds = thresholds || [0, 22, 48, 75, 110];

  const activeCompany = activeThreshold(currentThresholds);

  const oneElementMode =
    oneElementTitle && oneElementSubtitle && oneElementDescription;

  return (
    <Section width="small">
      {!oneElementMode && (
        <>
          <div ref={ref} style={{ position: 'relative', top: '-80px' }} />
          <StyledStack {...props} sx={sx}>
            <StyledTextWrapper
              spacing={4}
              descMarginBottom={descMarginBottom}
              height={height}>
              <Box sx={{ display: 'flex' }}>
                {image ? (
                  <Box>
                    <img src={image} width={100} alt="" />
                  </Box>
                ) : null}
                <StyledTitle color={colorWhite} variant="display_xs">
                  {title}
                </StyledTitle>
              </Box>
              <Typography color={colorWhite} variant="regular_400">
                {parse(description || '')}
              </Typography>
              {CustomButton}
            </StyledTextWrapper>
            <StyledRightWrapper ref={containerRef}>
              <Line />
              <StyledOffersWrapper spacing={18}>
                {data?.map((item, index) => {
                  return (
                    <OfferBox
                      key={index}
                      {...item}
                      isActive={index === activeCompany}
                      height={height}
                    />
                  );
                })}
              </StyledOffersWrapper>
            </StyledRightWrapper>
          </StyledStack>
        </>
      )}
      {oneElementMode && (
        <>
          <div ref={ref} style={{ position: 'relative', top: '-80px' }} />
          <StyledOneElement spacing={4}>
            <Typography variant="heading3" color="white">
              {oneElementTitle}
            </Typography>
            <Typography variant="subtitle" color="white">
              {oneElementSubtitle}
            </Typography>
            <Typography variant="body2Small" color="white">
              {oneElementDescription}
            </Typography>
          </StyledOneElement>
        </>
      )}
    </Section>
  );
});
